import { JSONNode } from '@atlaskit/editor-json-transformer';

export type AnalyseContentResult = {
	taskListLengths: Record<number, number>; // Counter for taskList items indexed by number of their taskItems
	hasMentions: boolean; // Flag whether any of the taskList items contains a mention item
	linkDomains: Record<string, number>; // How many links were saved
	numGoogleSpreadsheets: number; // How many links contain google spreadsheet URL
};

function analyseTaskList(taskList: JSONNode, result: AnalyseContentResult) {
	const { taskListLengths } = result;

	const taskItems = taskList.content || [];
	const taskItemLength = taskItems.length;

	if (!taskListLengths[taskItemLength]) {
		taskListLengths[taskItemLength] = 0;
	}

	taskListLengths[taskItemLength]++;

	// We only need to find one "mention" item
	if (!result.hasMentions) {
		outerLoop: for (let i = 0; i < taskItems.length; i++) {
			const taskItemContent = taskItems[i]?.content;
			if (taskItemContent) {
				for (let j = 0; j < taskItemContent.length; j++) {
					if (taskItemContent[j]?.type === 'mention') {
						result.hasMentions = true;
						break outerLoop;
					}
				}
			}
		}
	}
}

function analyseParagraph(paragraph: JSONNode, result: AnalyseContentResult) {
	const paragraphItems = paragraph.content || [];

	const { linkDomains } = result;

	paragraphItems.forEach((content) => {
		if (content?.attrs && 'url' in content?.attrs) {
			const url = new URL(content.attrs.url as string);

			if (!linkDomains[url.hostname]) {
				linkDomains[url.hostname] = 0;
			}

			linkDomains[url.hostname]++;

			if (url.hostname === 'docs.google.com' && url.pathname.includes('spreadsheets')) {
				result.numGoogleSpreadsheets++;
			}
		}
	});
}

export function analyseContent(content: JSONNode[]) {
	try {
		const result: AnalyseContentResult = {
			taskListLengths: {},
			hasMentions: false,
			linkDomains: {},
			numGoogleSpreadsheets: 0,
		};

		// Limit to first 50 tasklists for efficiency reasons
		const contentNodesMax = Math.min(content.length, 50);
		for (let i = 0; i < contentNodesMax; i++) {
			switch (content[i].type) {
				case 'taskList':
					analyseTaskList(content[i], result);
					break;
				case 'paragraph':
					analyseParagraph(content[i], result);
					break;
				default:
				// do nothing
			}
		}

		return result;
	} catch (error: unknown) {
		if (error && typeof error === 'object' && 'message' in error) {
			return {
				errorMessage: error.message,
			};
		}

		return {
			errorMessage: 'unknown',
		};
	}
}
